import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GateCanvasInfo, GateChild, Recipe, RecipeWidget, WidgetState } from '@kemu-io/kemu-core/types';
import {
  selectExecutionMode,
  selectEditingSnapshot,
  selectEditingModeWidgetIds,
} from '@src/features/Workspace/workspaceSlice';
import { safeJSONStringify } from '../utils';

/**
 * Represents the run mode status of a widget.
 */
export type WidgetRunModeStatus = {
  /** Whether the widget is new (did not exist in edit mode) */
  isNew: boolean;
  /** Whether the widget state has changed compared to edit mode snapshot */
  hasStateChanges: boolean;
  /** Whether the widget position has changed compared to edit mode snapshot */
  hasPositionChanges: boolean;
  /** Whether the widget connections have changed compared to edit mode snapshot */
  hasConnectionChanges: boolean;
  /** Whether the widget should be highlighted (isNew or has any changes) */
  shouldHighlight: boolean;
  /** The type of highlight to apply */
  highlightType: 'none' | 'new' | 'state' | 'position' | 'connection';
};

/**
 * Retrieves the full widget from a recipe snapshot.
 * @param snapshot - the recipe snapshot to read from.
 * @param thingId - the id of the thing containing the widget.
 * @param widgetId - the id of the widget.
 * @returns the widget or null if not found.
 */
const getWidgetFromSnapshot = (
  snapshot: Recipe,
  thingId: string,
  widgetId: string
): RecipeWidget | null => {
  const block = snapshot.blocks?.[thingId];
  if (!block) { return null; }

  const widget = block.gates?.[widgetId];
  if (!widget) { return null; }

  return widget;
};

const NO_CHANGES: WidgetRunModeStatus = {
  isNew: false,
  hasStateChanges: false,
  hasPositionChanges: false,
  hasConnectionChanges: false,
  shouldHighlight: false,
  highlightType: 'none',
};

/**
 * Hook that determines if a widget should be highlighted in run mode.
 * A widget is highlighted if it's new (added during run mode) or if its state,
 * position, or connections have changed compared to the edit mode snapshot.
 * @param recipePoolId - the id of the recipe in the pool.
 * @param thingId - the id of the thing containing the widget.
 * @param widgetId - the id of the widget.
 * @param currentState - the current state of the widget.
 * @param currentCanvas - the current canvas info of the widget (for position tracking).
 * @param currentChildren - the current children of the widget (for connection tracking).
 * @returns an object indicating the run mode status of the widget.
 */
const useWidgetRunModeStatus = (
  recipePoolId: string,
  thingId: string,
  widgetId: string,
  currentState: WidgetState,
  currentCanvas: GateCanvasInfo,
  currentChildren: GateChild[]
): WidgetRunModeStatus => {
  const executionMode = useSelector(selectExecutionMode);
  const editingSnapshot = useSelector(selectEditingSnapshot);
  const editingModeWidgetIds = useSelector(selectEditingModeWidgetIds);

  // Serialize current values for comparison and memoization dependencies
  const currentStateStr = safeJSONStringify(currentState, true);
  const currentCanvasStr = JSON.stringify(currentCanvas || {});
  const currentChildrenStr = JSON.stringify(currentChildren || []);

  const status = useMemo<WidgetRunModeStatus>(() => {
    // Not in execution mode, no highlighting needed
    if (!executionMode) {
      return NO_CHANGES;
    }

    const compositeId = `${thingId}:${widgetId}`;

    // Check if widget is new (didn't exist in edit mode)
    const isNew = editingModeWidgetIds
      ? !editingModeWidgetIds.includes(compositeId)
      : false;

    // If widget is new, no need to compare state
    if (isNew) {
      return {
        isNew: true,
        hasStateChanges: false,
        hasPositionChanges: false,
        hasConnectionChanges: false,
        shouldHighlight: true,
        highlightType: 'new',
      };
    }

    if (!editingSnapshot) {
      return NO_CHANGES;
    }

    const snapshotWidget = getWidgetFromSnapshot(editingSnapshot, thingId, widgetId);
    if (!snapshotWidget) {
      return NO_CHANGES;
    }

    // Check state changes
    const snapshotStateStr = safeJSONStringify(snapshotWidget.state, true);
    const hasStateChanges = currentStateStr !== snapshotStateStr;

    // Check canvas (position) changes
    const snapshotCanvasStr = JSON.stringify(snapshotWidget.canvas || {});
    const hasPositionChanges = currentCanvasStr !== snapshotCanvasStr;

    // Check connection (children) changes
    const snapshotChildrenStr = JSON.stringify(snapshotWidget.children || []);
    const hasConnectionChanges = currentChildrenStr !== snapshotChildrenStr;

    const shouldHighlight = hasStateChanges || hasPositionChanges || hasConnectionChanges;

    // Determine highlight type (priority: state > connection > position)
    let highlightType: WidgetRunModeStatus['highlightType'] = 'none';
    if (hasStateChanges) {
      highlightType = 'state';
    } else if (hasConnectionChanges) {
      highlightType = 'connection';
    } else if (hasPositionChanges) {
      highlightType = 'position';
    }

    return {
      isNew: false,
      hasStateChanges,
      hasPositionChanges,
      hasConnectionChanges,
      shouldHighlight,
      highlightType,
    };
  }, [
    executionMode,
    editingSnapshot,
    editingModeWidgetIds,
    thingId,
    widgetId,
    currentStateStr,
    currentCanvasStr,
    currentChildrenStr,
  ]);

  return status;
};

export default useWidgetRunModeStatus;
